/**
 * Phase H6d — shared primitives + constants for the student-import
 * surface, extracted from the wizard's first-run page so the new
 * standing-importer (`/imports/students`) can reuse the same UI
 * vocabulary without duplicating CSS, copy, or contract details.
 *
 * The wizard owns one orchestration shape (3 steps, "skip & finish"
 * exits, "Continue to dashboard" CTA on done), and the standing
 * importer owns another (single-step, "Import another batch" reset
 * CTA on done, AppShell chrome instead of FirstRunShell). Anything
 * SHARED between the two — verdict pills, dropzone, summary card,
 * dynamic-columns panel, byte formatting, error-message mapping —
 * lives in this directory and is re-rendered by both orchestrators
 * with no behavioural drift.
 *
 * Keep this file framework-agnostic (no React imports) — JSX
 * components sit in sibling files and import these constants.
 */

import type { StudentImportRowVerdict } from "@eduviaos/shared";
import { ApiError } from "@eduviaos/shared";

export const VERDICT_LABEL: Record<StudentImportRowVerdict, string> = {
  fresh: "Fresh",
  duplicate_email: "Duplicate (email)",
  duplicate_composite: "Duplicate (name + DOB + parent phone)",
  duplicate_in_payload: "Duplicate in upload",
  invalid_row: "Invalid row",
};

export const VERDICT_VARIANT: Record<StudentImportRowVerdict, "success" | "warn" | "danger"> = {
  fresh: "success",
  duplicate_email: "warn",
  duplicate_composite: "warn",
  duplicate_in_payload: "warn",
  invalid_row: "danger",
};

/** Hard cap on uploadable file size — keep aligned with the BE's
 *  `import_routes` per-call payload limit. The standing endpoint has
 *  the same ceiling as the wizard endpoint by design (one importer
 *  service, two consumer routes), so this constant is shared. */
export const MAX_FILE_BYTES = 1 * 1024 * 1024;

/** Accept-list passed to `<input type="file">`. We intentionally allow
 *  `text/plain` because some macOS exporters mis-stamp CSV files with
 *  the plain-text mime; the BE parser checks for header rows, so a
 *  non-CSV file is rejected server-side anyway with a clear message. */
export const ACCEPTED_TYPES = ".csv,text/csv,application/vnd.ms-excel,text/plain";

export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

/**
 * Friendly error message for the upload-side flow. The BE returns
 * structured `ApiError` instances with codes; we recognise the two
 * common ones and fall through to the server's message for anything
 * else (which the BE writes in friendly tones already). The "Something
 * went wrong" branch covers network errors and non-API exceptions.
 */
export function messageFor(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.code === "INVALID_CSV") {
      return (
        "We couldn't parse that CSV. " +
        (err.message || "Make sure it's a valid UTF-8 file with a header row.")
      );
    }
    if (err.code === "ROW_LIMIT_EXCEEDED") {
      return (
        "That file has too many rows for one batch. Split it into chunks of " +
        "500 or fewer and try again."
      );
    }
    return err.message || "Something went wrong. Try again in a moment.";
  }
  return "Something went wrong. Try again in a moment.";
}
